import React from 'react';
import MuiMenuItem from '@mui/material/MenuItem';
import MuiDivider from '@mui/material/Divider';
import { alpha, styled } from '@mui/material/styles';

import { radius } from '../theme/tokens.radius';
import { colors } from '../theme/tokens.colors';
import { textColors } from '../theme/uikitPalette';

interface UIKitMenuItemProps {
  text: string;
  onClick?: () => void;
  disabled?: boolean;
  height?: 'compact' | 'tall';
  leading?: React.ReactNode;
  trailing?: React.ReactNode;

  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;

  divider?: boolean;
  className?: string;
  style?: React.CSSProperties;
  selected?: boolean;
}

const StyledMenuItem = styled(MuiMenuItem, {
  shouldForwardProp: (prop) => prop !== 'tall',
})<{ tall?: boolean }>(({ tall }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',

  paddingTop: tall ? 12 : 6,
  paddingBottom: tall ? 12 : 6,
  paddingLeft: 16,
  paddingRight: 16,

  minHeight: 0,
  borderRadius: radius.xs,

  backgroundColor: colors.specialColors.white,

  // tipografía base
  fontFamily: 'inherit',
  fontSize: 14,
  lineHeight: 1.5,

  color: textColors.primary,

  '&:hover': {
    backgroundColor: alpha(textColors.primary, 0.08),
  },

  // disabled={disabled} ya aplica la clase nativa Mui-disabled de MUI - se
  // usa esa en vez del truco JSS "&$disabled" (una clase vacía toggleada a
  // mano) que tenía la versión con makeStyles.
  '&.Mui-disabled': {
    color: textColors.disabled,
  },
}));

const Left = styled('span')({
  marginRight: 16,
  display: 'inline-flex',
  alignItems: 'center',
});

const Right = styled('span')({
  marginLeft: 16,
  display: 'inline-flex',
  alignItems: 'center',
});

const Label = styled('span')({
  flex: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const StyledDivider = styled(MuiDivider)({
  marginLeft: 16,
  marginRight: 16,
});

const UIKitMenuItem = React.forwardRef<HTMLLIElement, UIKitMenuItemProps>(
  (
    {
      text,
      onClick,
      disabled = false,
      height = 'compact',

      leading,
      trailing,

      iconLeft,
      iconRight,

      divider = false,
      selected = false,
      className,
      style,
    },
    ref
  ) => {
    const item = (
      <StyledMenuItem
        ref={ref} // 👈 el ref que MUI necesita para foco / teclado
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        tall={height === 'tall'}
        className={className}
        selected={selected}
        style={style}
      >
        {/* leading tiene prioridad, pero mantenés compatibilidad con iconLeft */}
        {(leading || iconLeft) && <Left>{leading || iconLeft}</Left>}

        <Label>{text}</Label>

        {/* trailing o iconRight */}
        {(trailing || iconRight) && <Right>{trailing || iconRight}</Right>}
      </StyledMenuItem>
    );

    if (!divider) {
      return item;
    }

    return (
      <>
        {item}
        <StyledDivider />
      </>
    );
  }
);

export default UIKitMenuItem;
